import { AfterViewInit, Component, ElementRef, EventEmitter, Inject, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild, ViewContainerRef, ViewEncapsulation } from '@angular/core';

import { util } from '../../../../classes/util';
import { ESRI } from '../../../../esri-loader-util';
import { InputConverter } from '../../../../inputConverter';
import { ILoggingService } from '../../../../services/interfaces/ILoggingService';
import { IUserSettingsService } from '../../../../services/interfaces/IUserSettingsService';
import { LayerService } from '../../../../services/layer.service';
import { MeasureState } from '../../../../types';
import { BaseComponent } from '../../../base.component';

import AreaMeasurementConfiguration, { AreaMeasureConfigurationConverter } from './area.config';

const initilizeDebounceTime = 50;

@Component({
    encapsulation: ViewEncapsulation.ShadowDom,
    selector: 'mc-area-measurement-ng',
    styleUrls: ['../../../sharedStyles/base.scss', './area.component.scss'],
    templateUrl: './area.component.html'
})
/**
 * Component implementing the ESRI AreaMeasurement2D tool
 *
 * Usage:
 *
 * ```html
 * <mc-area-measurement [areaMeasureConfig]="areaMeasureConfig"></mc-area-measurement>
 * ```
 *
 * ```typescript
 *
 * ```
 *
 * See the [[__esri.AreaMeasurement2DProperties]] for more details.
 *
 */
export class AreaComponent extends BaseComponent implements AfterViewInit, OnChanges, OnDestroy, OnInit {
    areaMeasurement2D: __esri.AreaMeasurement2D;
    private mapView: __esri.MapView;

    /**
     * Class that determines which icon is used for the button
     */
    @Input() toolTip?: string;

    /**
     * Class that determines which icon is used for the button.
     *
     * @Default 'esri-icon-minus'
     */
    @Input() iconClass?: string;

    /**
     * Overall state of the measure component. Values: true = User may draw measure line on map, false = tool is inactive.
     */
    @Input() @InputConverter() set active(value: boolean) {
        this._active = value;
        value ? this.activate() : this.deactivate();
        this.activeChange.emit(value);
    }
    get active(): boolean { return this._active; }
    @Output() readonly activeChange: EventEmitter<boolean> = new EventEmitter();
    private _active: boolean = false;

    /**
     * Boolean flag indicating if the measurement should be turned into a layer automatically when measure is completed
     *
     * @Default false
     */
    @Input() set autolayer(value: boolean) {
        this._autolayer = value;
        if (this._autolayer &&
            this.areaMeasurement2D.viewModel.measurement &&
            !(this.areaMeasurement2D.viewModel as any).active) {
            this.addAsLayer(this.areaMeasurement2D.viewModel.measurement, this.areaMeasurement2D.viewModel.measurementLabel);
            this.areaMeasurement2D.viewModel.start();
        }
    }
    get autolayer(): boolean { return this._autolayer; }
    private _autolayer: boolean = false;

    /**
     * Object containing default settings for this component.
     */
    @Input()
    @InputConverter(AreaMeasureConfigurationConverter)
    areaMeasurementConfigs: AreaMeasurementConfiguration = new AreaMeasurementConfiguration();

    /**
     * Measure component will find users default values and give those priority over other settings.
     */
    @Input() includeUserDefault = true;

    /**
     * The threshold distance used by the "auto" mode to switch between planar and geodesic linear computations. The threshold is measured in meters.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-AreaMeasurement2D-AreaMeasurement2DViewModel.html#geodesicDistanceThreshold)
     *
     */
    @Input() set geodesicDistanceThreshold(value: number) {
        this._geodesicDistanceThreshold = value;
        if (this.areaMeasurement2D) {
            this.areaMeasurement2D.viewModel.geodesicDistanceThreshold = value;
        }
    }
    get geodesicDistanceThreshold(): number {
        return (this._geodesicDistanceThreshold = this.areaMeasurement2D ? this.areaMeasurement2D.viewModel.geodesicDistanceThreshold : this._geodesicDistanceThreshold);
    }
    private _geodesicDistanceThreshold: number;

    /**
     * Display the Measure interface while drawing measurement.
     *
     * When set to false the user will be able to draw the distance line but not start a new distance line.
     *
     * @Default true
     *
     */
    @Input() showDialog = true;

    /**
     * State of the measurement tool. Values: 'disabled', 'ready', 'measuring', 'measured'
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-AreaMeasurement2D-AreaMeasurement2DViewModel.html#state)
     */
    set state(value: MeasureState) {
        this._state = value;
        this.stateChange.emit(value);
    }
    get state(): MeasureState { return this._state; }
    @Output() readonly stateChange: EventEmitter<MeasureState> = new EventEmitter();
    private _state: MeasureState = 'disabled';

    /**
     * Unit system (imperial, metric) or specific unit used for displaying the distance values.  **Possible Values:** metric | imperial | inches | feet | us-feet | yards | miles | nautical-miles | meters | kilometers
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-AreaMeasurement2D-AreaMeasurement2DViewModel.html#unit)
     *
     * @Default imperial
     */
    @Input() set unit(value: __esri.SystemOrAreaUnit) {
        this._unit = value;
        if (this.areaMeasurement2D) {
            this.areaMeasurement2D.viewModel.unit = value;
        }
    }
    get unit(): __esri.SystemOrAreaUnit {
        return (this._unit = this.areaMeasurement2D ? this.areaMeasurement2D.viewModel.unit : this._unit);
    }
    private _unit: __esri.SystemOrAreaUnit;

    /**
     * List of available units and unit systems (imperial, metric) for displaying the distance values. By default, the following units are included: `metric`, `imperial`, `inches`, `feet`, `us-feet`, `yards`, `miles`, `nautical-miles`, `meters`, `kilometers`.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-AreaMeasurement2D-AreaMeasurement2DViewModel.html#unitOptions)
     */
    @Input() set unitOptions(value: __esri.SystemOrAreaUnit[]) {
        this._unitOptions = value;
        if (this.areaMeasurement2D) {
            this.areaMeasurement2D.viewModel.unitOptions = value;
        }
    }
    get unitOptions(): __esri.SystemOrAreaUnit[] {
        return (this._unitOptions = this.areaMeasurement2D ? this.areaMeasurement2D.viewModel.unitOptions : this._unitOptions);
    }
    private _unitOptions: __esri.SystemOrAreaUnit[];

    @Output() readonly onmeasure: EventEmitter<__esri.AreaMeasurement2DViewModel> = new EventEmitter();

    @ViewChild('body') body: ElementRef<any>;

    constructor(
        _vcr: ViewContainerRef,
        private readonly layerService: LayerService,
        @Inject('LoggingService') public LoggingService: ILoggingService,
        @Inject('UserSettingsService') public UserSettingsService: IUserSettingsService
    ) {
        super(_vcr);
        this.registerEvent('stateChange', this.stateChange);
        this.registerEvent('activeChange', this.activeChange);
        this.registerEvent('onmeasure', this.onmeasure);
    }

    async ngOnInit() {
        try {
            // Handle defaults
            this.setupAttributeDefaults(this.areaMeasurementConfigs);
            if (this.includeUserDefault) {
                await this.setupUserDefault();
            }
            this.setProperties(this.areaMeasurementConfigs);
            await this.setMapViewRef();
        }
        catch (err) {
            this.readyProblem(err);
            throw err;
        }

    }

    async ngAfterViewInit() {
        this.isReady();
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.active && !changes.active.firstChange) {
            changes.active.currentValue ? this.activate() : this.deactivate();
            this.activeChange.emit(changes.active.currentValue);
        }
    }

    async ngOnDestroy() {
        this.areaMeasurement2D = null;
        this.areaMeasurementConfigs = null;
    }

    initializing = false;
    private readonly listners: IHandle[] = [];
    /**
     * Make the distance measurement tool active/shown.
     */
    private async activate() {
        try {
            if (!this.areaMeasurement2D) {
                this.initializing = true;
                const stopInitialCheck = util.debounce(() => { this.initializing = false; }, initilizeDebounceTime);
                this.listners.push(this.layerService.mapRef.allLayers.on('before-add', (event) => {
                    if (this.initializing && event.item.listMode === 'hide') {
                        event.item.set('hideInLegend', true);
                        event.item.set('areaMeasureToolGeneratedLayer', true);
                        stopInitialCheck();
                    }
                }));
                this.listners.push(this.layerService.mapRef.allLayers.on('after-remove', (event) => {
                    if (event.item.get<boolean>('areaMeasureToolGeneratedLayer')) {
                        this.initializing = true;
                    }
                }));

                this.areaMeasurement2D = await this._createAreaMeasurement2D(this.areaMeasurementConfigs);
                this.areaMeasurement2D.viewModel.start();
            }
        }
        catch (err) {
            this.readyProblem(err);
            throw err;
        }
    }

    /**
     * Make the distance measurement tool inactive/hidden.
     */
    private deactivate(): void {
        if (this.areaMeasurement2D) {
            this.areaMeasurement2D.destroy();
            this.areaMeasurement2D = null;
        }
        while (this.listners.length) {
            this.listners.pop().remove();
        }
    }

    /**
     * Create a AreaMeasurement2D widgit and return it to caller.
     */
    private async _createAreaMeasurement2D(config: AreaMeasurementConfiguration) {

        // Create a temporary <div> for the widget, it will remove this when it is destroyed.
        const newElement = this.body.nativeElement.appendChild(document.createElement('div'));

        // Create tool
        const AreaMeasurement2D = await ESRI.AreaMeasurement2D;
        const areaMeasurement2D = new AreaMeasurement2D({
            container: newElement,
            viewModel: {
                unit: config.unit,
                unitOptions: config.unitOptions,
                geodesicDistanceThreshold: config.geodesicDistanceThreshold
            }
        } as any);
        areaMeasurement2D.view = this.mapView;

        this.state = 'ready'; // Widget doesn't call the watcher for its first value; manually set our state

        // Add watchers
        areaMeasurement2D.watch('viewModel.state', (value) => {
            this.state = value;
        });
        areaMeasurement2D.watch('viewModel.active', async (newValue, oldValue) => {
            if (!newValue && oldValue && areaMeasurement2D.viewModel) {
                this.onmeasure.emit(areaMeasurement2D.viewModel);
                this.state = 'measured'; // ESRI component has code for 'measured' state but does not yet apply it? Manually set for now.
                if (this.autolayer) {
                    await this.addAsLayer(areaMeasurement2D.viewModel.measurement, areaMeasurement2D.viewModel.measurementLabel);
                    this.areaMeasurement2D.viewModel.start();
                }
            }
        });
        return areaMeasurement2D;
    }

    /**
     * Add completed measure geometry as a layer to the map
     */
    async addAsLayer(measurement: __esri.AreaMeasurement2DViewModelMeasurement, label: __esri.AreaMeasurement2DViewModelMeasurementLabel) {
        let Graphic: __esri.GraphicConstructor;
        let GraphicsLayer: __esri.GraphicsLayerConstructor;
        [Graphic, GraphicsLayer] = await Promise.all([ESRI.Graphic, ESRI.GraphicsLayer]);
        const graphic = new Graphic({
            geometry: measurement.geometry,
            symbol: {
                type: 'simple-fill',
                color: [0, 0, 0],
                style: 'none',
                outline: {
                    color: [0, 128, 255],
                    style: 'solid',
                    width: 3
                }
            } as __esri.SimpleFillSymbolProperties
        } as __esri.GraphicProperties);
        const layer = new GraphicsLayer({
            title: `Area: ${label.area}`,
            graphics: [graphic]
        });
        layer.set('measureLayer', 'area');
        this.mapView.map.add(layer);
    }

    /**
     * Apply defaults passed in through attributes of this component.
     */
    private setupAttributeDefaults(config: AreaMeasurementConfiguration) {
        ['label', 'toolTip', 'icon', 'color', 'geodesicDistanceThreshold', 'mode', 'unit', 'unitOptions'].forEach((prop) => {
            if (typeof this[prop] !== 'undefined') {
                config[prop] = this[prop];
            }
        });
    }

    /**
     * Apply user specific defaults for this components behavior.
     */
    private async setupUserDefault() {
        if (this.includeUserDefault) {
            // FIXME: Instead of using default apply User setttings here.
            // const userAreaMeasureConfiguration = await this.UserSettingsService.GetAreaMeasurementSettings();
            const userAreaMeasureConfiguration = new AreaMeasurementConfiguration();
            if (userAreaMeasureConfiguration) {
                Object.assign(this.areaMeasurementConfigs, userAreaMeasureConfiguration);
            }
        }
    }

    /**
     * Set undefined primary properties of this component with user/config properties.
     */
    private setProperties(config: AreaMeasurementConfiguration) {
        ['label', 'toolTip', 'icon', 'color'].forEach((prop) => {
            if (typeof this[prop] === 'undefined' && (typeof config[prop] !== 'undefined')) {
                this[prop] = config[prop];
            }
        });
    }

    /**
     * Set private reference to this components mapView.
     */
    private async setMapViewRef() {
        if (!this.mapView) {
            const parent = await this.getClosestAncestor('mc-map');
            if (parent) {
                await parent.ready;
                this.mapView = parent.mapView;
            } else {
                this.readyProblem('Map View Reference not supplied and reference from parent element could not be retrieved.');
            }
        }
    }
}
